import './styles/mapboxSymbol.css'

import type { ITrip } from '@constants/types'
import type { MapDrawerHandle } from '@containers/admin/Trip/MapDrawer'
import { mekkaLocation } from '@containers/MekkahPage'
import { getServerUrl } from '@hooks/useApi'
import { asArray } from '@shared_utils/array_utils'
import { waypointInfoFromGpxElement } from '@utils/gpx_utils'
import { isValidLatitude, isValidLongitude } from 'is-valid-geolocation'
import {
  FullscreenControl,
  LngLat,
  LngLatBounds,
  type LngLatLike,
  type Map,
  Marker,
  type MarkerOptions,
  MercatorCoordinate,
  NavigationControl,
  Popup,
  type PopupOptions
} from 'mapbox-gl'
import type { RefObject } from 'react'

import { circularMove, drawSpanWithTransformation, drawTextWithTransformation } from '../mapOptions'
import { type MarkersPositionsAndCoords } from '../useMapboxMap'

export function createMapboxMarker (el?: HTMLElement, options?: MarkerOptions): Marker {
  if (el && options) {
    return new Marker(el, options)
  } else if (el) {
    return new Marker(el)
  } else if (options) {
    return new Marker(options)
  } else {
    return new Marker()
  }
}

export function drawCompass (map: Map, coords: LngLatLike) {
  const innerCompassMarker = document.createElement('div')
  innerCompassMarker.className = 'compass-marker'

  const outerCompassMarker = document.createElement('div')
  outerCompassMarker.className = 'compass-marker-point'
  outerCompassMarker.appendChild(innerCompassMarker)

  const compassMarker = createMapboxMarker(outerCompassMarker, {
    rotationAlignment: 'viewport'
  })
  compassMarker.setLngLat(coords)
  compassMarker.addTo(map)

  const radius = outerCompassMarker.getBoundingClientRect().width / 2
  let index = 0

  for (let i = 0; i < 8; i++) {
    const line = circularMove(radius, i, 45)
    if (i % 2) {
      drawSpanWithTransformation(line.x, line.y, i, innerCompassMarker)
    } else {
      const text = circularMove(radius + 20, i, 45)
      drawSpanWithTransformation(line.x, line.y, i, innerCompassMarker, 'bolder')
      drawTextWithTransformation(text.x, text.y, index, innerCompassMarker)
      index++
    }
  }

  return compassMarker
}

export function createYachtMarker () {
  const div = document.createElement('div')
  div.className = 'yacht-marker-wrapper'
  div.innerHTML = `<div class="mekkah-yacht-marker dot" data-markertype="current-location">
    <span data-markertype="current-location"></span>
    <span data-markertype="current-location"></span>
    <span data-markertype="current-location"></span>
  </div>`

  return createMapboxMarker(div, { scale: 1 })
}

export function drawStartEndMarkers (map: Map, data: MarkersPositionsAndCoords, trip?: ITrip) {
  if (!data) return { startMarker: null, endMarker: null }

  const { markerCoords } = data
  const divStart = document.createElement('div')
  const divEnd = document.createElement('div')

  divStart.innerHTML = '<div class="markerContent"><div class="waypointss"></div></div>'
  divEnd.innerHTML = '<div class="markerContent"><div class="waypointss"></div></div>'

  const startMarker = createMapboxMarker(divStart).setLngLat([markerCoords.lngStart, markerCoords.latStart])

  const endMarker = createMapboxMarker(divEnd).setLngLat([markerCoords.lngEnd, markerCoords.latEnd])

  if (trip && trip.imagePath && trip.destinationName) {
    const endMarkerPopup = new Popup()
      .setLngLat([markerCoords.lngEnd, markerCoords.latEnd])
      .setHTML(createDestinationPopupContent(trip))
      .addTo(map)

    endMarkerPopup.addClassName('destination-marker-popup')

    endMarker.setPopup(endMarkerPopup)
  }

  startMarker.addTo(map)
  endMarker.addTo(map)

  return { startMarker, endMarker }
}

export function createHighlightsMarkers (highlights: Element[], highlightsMarkers: Marker[]): (Marker | null)[] {
  return asArray(highlights).map(highlight => {
    if (isHighlightOnMap(highlight, highlightsMarkers)) {
      return null
    }
    return createHighlightMarker(highlight)
  })
}

export function clearHighlightsMarkers (highlightsMarkers: Marker[], map: Map) {
  if (!highlightsMarkers || !map) {
    return []
  }

  for (const highlight of highlightsMarkers) {
    highlight.remove()
  }
  return
}

export declare interface MapboxContextMenuItem {
  text: string
}

export declare interface MapboxContextMenuOptions {
  items: MapboxContextMenuItem[]
  onClick: (index: number) => void
}

export function createMapboxContextMenu (menuOptions: MapboxContextMenuOptions, options?: PopupOptions): Popup {
  const menuDiv = document.createElement('div')
  menuOptions.items.forEach((i, index) => {
    const linkEl = document.createElement('a')
    linkEl.style.color = 'white'
    linkEl.text = i.text
    linkEl.addEventListener('click', () => {
      menuOptions.onClick(index)
    })

    const sm = document.createElement('small')
    sm.appendChild(linkEl)

    const para = document.createElement('p')
    para.appendChild(sm)

    menuDiv.appendChild(para)
  })

  return createMapboxPopup(options).setDOMContent(menuDiv)
}

export function drawMekkah (map: Map) {
  const div = document.createElement('div')
  div.className = 'mekkah-marker-point'
  div.innerHTML = '<div class="mekkah-marker"><div>'

  return createMapboxMarker(div).setLngLat(mekkaLocation).addTo(map)
}

function isHighlightOnMap (highlight: Element, highlightsMarkers: Marker[]) {
  const el = highlight.getElementsByTagName('poiId')[0]
  const highlightId = el?.textContent || ''
  return highlightsMarkers.find(marker => marker.getElement().id === highlightId)
}

function createHighlightMarker (highlight: Element) {
  const { poiLat, poiLng, poiId } = waypointInfoFromGpxElement(highlight)
  const highlightMarkerDiv = document.createElement('div')
  highlightMarkerDiv.setAttribute('id', poiId.toString())
  highlightMarkerDiv.className = 'mapboxgl__highlight'

  return createMapboxMarker(highlightMarkerDiv).setLngLat([+poiLng, +poiLat])
}

export function createMapboxNavigationControl () {
  return new NavigationControl()
}

export function createFullScreenControl (): FullscreenControl {
  return new FullscreenControl()
}

export function createMapboxPopup (options?: PopupOptions): Popup {
  return new Popup(options)
}

export function createMapboxLngLatBounds (
  boundsLike?: [LngLatLike, LngLatLike] | [number, number, number, number]
): LngLatBounds {
  return new LngLatBounds(boundsLike)
}

export function createLngLat (lng: number, lat: number): LngLat {
  return new LngLat(lng, lat)
}

export function toLngLat (point: LngLatLike | { lat: number; lng?: number; lon?: number }): LngLat | null {
  if (Array.isArray(point)) return createLngLat(point[0], point[1])
  if (point instanceof LngLat) return point

  if ('lat' in point) {
    if ('lng' in point && point.lng != null) return createLngLat(point.lng, point.lat)
    if ('lon' in point && point.lon != null) return createLngLat(point.lon, point.lat)
  }

  return null
}

export function isValidLngLatLike (point: LngLatLike | { lat: number; lng?: number; lon?: number }) {
  if (!point) {
    return false
  }
  try {
    const l = toLngLat(point)
    if (l) {
      const { lng, lat } = l
      return isValidLongitude(lng) && isValidLatitude(lat)
    } else {
      return false
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return false
  }
}

export function createMercatorCoordinateFromLngLat (lngLatLike: LngLatLike, altitude?: number): MercatorCoordinate {
  return MercatorCoordinate.fromLngLat(lngLatLike, altitude)
}

const createDestinationPopupContent = (trip: ITrip) => {
  const { imagePath, destinationName, destinationDescription } = trip

  return `
    <div class="destination-popup">
      <div style="background-color: #47597a; border-radius: 10px 10px 0 0">
        ${imagePath && `<img src="${getServerUrl(imagePath)}" alt="yacht" class="destination-popup-image"/>`}
      </div>
      <div class="destination-popup-content">
        <h6 style="font-size: 0.875rem; font-weight: 500; text-align: center;">${destinationName}</h6>
        <h6 style="max-height: 150px; overflow: auto; font-size: 0.875rem; font-weight: 400;">${destinationDescription}</h6>
      </div>
    </div>
  `
}

export const createFormButton = (mapDrawerRef: RefObject<MapDrawerHandle | null>) => {
  const onAdd = () => {
    const div = document.createElement('div')
    div.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    div.innerHTML = `
      <button aria-label="Open/Close Form" title="Open/Close Form">
        <svg focusable="false" viewBox="0 0 32 32" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.5 8.62v6.76L5.12 12zM10 5l-7 7 7 7zm4 0v14l7-7z" transform="translate(4, 4)" />
        </svg>
      </button>
    `

    div.addEventListener('contextmenu', e => e.preventDefault())

    div.addEventListener('click', () => mapDrawerRef.current?.handleOpenDrawer())

    return div
  }

  const onRemove = () => {
    const element = document.querySelector('.mapboxgl-ctrl.mapboxgl-ctrl-group')
    if (element?.parentNode) {
      element.parentNode.removeChild(element)
    }
  }

  return { onAdd, onRemove }
}
